import _ from "lodash";
import {
  MarketplaceFormConfig,
  MarketplaceFormElement,
  MarketplaceFormElementType,
  MarketplaceFormElementValidation,
} from "../../schema/generated/schema";

type SelectOption = {
  value: string;
  label: string;
};

// Map select options to enum/enumNames to conform to RJSF custom labels for enum fields
// https://react-jsonschema-form.readthedocs.io/en/docs/usage/single/#custom-labels-for-enum-fields
function getEnumFields(options: readonly (string | SelectOption)[]) {
  return options.reduce(
    (enumFields, option) => {
      const enumValue = typeof option === "string" ? option : option.value;
      enumFields.enum.push(enumValue);

      const enumName =
        typeof option === "string" ? option : option.label || option.value;
      enumFields.enumNames.push(enumName);

      return enumFields;
    },
    { enum: [], enumNames: [] } as {
      enum: string[];
      enumNames: string[];
    }
  );
}

function addDefaultValueToEnum(
  type: {
    enum: string[];
    enumNames: string[];
  },
  defaultValue: any
) {
  // Need to add default value to BOTH enum and enumNames to avoid the
  // two lists getting out of sync and showing incorrect labels for values
  if (type.enum && !type.enum.includes(defaultValue)) {
    type.enum.unshift(defaultValue);
    type.enumNames.unshift(defaultValue);
  }
}

const {
  File,
  Select,
  Text,
  Number,
  Currency,
  Address,
  Bool,
  MultiSelect,
  Date,
} = MarketplaceFormElementType;
const { Email } = MarketplaceFormElementValidation;

type SchemaProps = { title?: string; description?: string };

const numericTypeConverter = ({
  label,
  defaultValue,
  min,
  max,
}: MarketplaceFormElement) => {
  const defaultNum = parseInt(defaultValue, 10);
  const type: any = {
    type: "number",
    title: label,
  };
  if (!_.isNaN(defaultNum)) {
    type.default = defaultNum;
  }
  if (
    _.isNumber(min) &&
    _.isNumber(max) &&
    !(min === 0 && max === 0) &&
    max > min
  ) {
    type.minimum = min;
    type.maximum = max;
  }
  return type;
};

const addressTypeConverter = ({
  label,
  description,
}: MarketplaceFormElement) => {
  return {
    title: label,
    ...(description && { description }),
    required: [
      "street1",
      "street2",
      "city",
      "state",
      "zipcode",
      "country",
      "phoneNumber",
    ],
    properties: {
      name: { type: ["string", "null"] },
      street1: { type: ["string", "null"] },
      street2: { type: ["string", "null"] },
      city: { type: ["string", "null"] },
      state: { type: ["string", "null"] },
      zipcode: { type: ["string", "null"] },
      country: { type: ["string", "null"] },
      phoneNumber: { type: ["string", "null"] },
      emailAddress: { type: ["string", "null"] },
    },
  };
};

const regExpValidations = {
  [Email]:
    "^[a-zA-Z0-9.!#$%&‘*+/=?^_`{|}~-]+@(?!.*\\.\\.)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$",
};

const jsonSchemaConverters: {
  [key: string]: (element: MarketplaceFormElement) => any;
} = {
  [Text]: ({ label, defaultValue, regex, validation }) => {
    let pattern = regex;
    if (validation && regExpValidations[validation]) {
      pattern = regExpValidations[validation];
    }
    const type: any = {
      type: "string",
      title: label,
      ...(pattern && { pattern }),
    };
    if (defaultValue) {
      type.default = defaultValue;
    }
    return type;
  },
  [Number]: numericTypeConverter,
  [Currency]: numericTypeConverter,
  [Address]: addressTypeConverter,
  [File]: ({ label, defaultValue, description }) => {
    const type: any = {
      type: "object",
      title: label,
      required: [],
      properties: {
        id: { type: "string" },
        originalFilename: { type: "string" },
        fileExtension: { type: "string" },
      },
      ...(description && { description }),
    };
    if (defaultValue) {
      type.default = defaultValue;
    }
    return type;
  },
  [Select]: ({
    label,
    defaultValue,
    optionValues,
    optionType,
    optionSourceExternalId,
  }) => {
    const enumFields =
      optionValues && optionValues !== null ? getEnumFields(optionValues) : {};
    const type: any = {
      type: "string",
      title: label,
      ...enumFields,
    };
    if (optionType && optionType !== "TEXT" && optionSourceExternalId) {
      // the options will be populated on the front end
      delete type.enum;
      delete type.enumNames;
    }
    if (defaultValue) {
      type.default = defaultValue;
      addDefaultValueToEnum(type, defaultValue);
    }
    return type;
  },
  [MultiSelect]: ({
    label,
    defaultValue,
    optionValues,
    required,
    optionType,
    optionSourceExternalId,
  }) => {
    const enumFields =
      optionValues && optionValues !== null ? getEnumFields(optionValues) : {};
    const type: any = {
      type: "string",
      title: label,
      ...enumFields,
    };
    if (optionType && optionType !== "TEXT" && optionSourceExternalId) {
      // the options will be populated on the front end
      delete type.enum;
      delete type.enumNames;
    }
    const elementSchema: any = {
      type: "array",
      title: label,
      items: type,
      uniqueItems: true,
    };
    if (defaultValue && typeof defaultValue === "string") {
      elementSchema.default = [defaultValue];
      addDefaultValueToEnum(type, defaultValue);
    }
    if (defaultValue && _.isArray(defaultValue)) {
      elementSchema.default = defaultValue;
      _.each(defaultValue, val => {
        addDefaultValueToEnum(type, val);
      });
    }
    if (required) {
      elementSchema.minItems = 1;
    }
    return elementSchema;
  },
  [Bool]: ({ label, defaultValue }) => {
    const type: any = {
      type: "boolean",
      title: label,
    };
    if (typeof defaultValue !== "undefined") {
      type.default = !!defaultValue;
    }
    return type;
  },
  [Date]: ({ label, defaultValue, dateOptionValues }) => {
    const type: any = {
      type: "string",
      title: label,
      dateOptionValues,
    };
    if (typeof defaultValue !== "undefined") {
      type.default = !!defaultValue;
    }
    return type;
  },
};

const lib: {
  generateSchema(
    name: string,
    config: MarketplaceFormConfig,
    props?: SchemaProps
  ): any;
} = {
  generateSchema(name, config, props): any {
    const { dependencies } = config;
    const elementMap: {
      [id: string]: MarketplaceFormElement;
    } = config.elements.reduce((memo, element) => {
      // eslint-disable-next-line no-param-reassign
      memo[element.key] = element;
      return memo;
    }, {} as any);
    const required: string[] = [];
    const properties = config.elementArrangement.reduce((memo, key) => {
      const element = elementMap[key];
      if (element.required) {
        required.push(key);
      }

      let schemaConverter = jsonSchemaConverters[element.type];

      // this is a fallback for element.type's that isn't already mapped like GROUP
      if (typeof schemaConverter !== "function") {
        schemaConverter = jsonSchemaConverters[Text];
      }
      // eslint-disable-next-line no-param-reassign
      memo[key] = schemaConverter(element);
      return memo;
    }, {} as any);

    const { title, description } = props || {};

    return {
      type: "object",
      title: title || name,
      ...(description && { description }),
      required,
      properties,
      ...(dependencies ? { dependencies } : {}),
    };
  },
};

export default lib;
